import { useEffect, useRef, useState } from 'react'

interface UseIntersectionObserverOptions {
  threshold?: number
  rootMargin?: string
  triggerOnce?: boolean
}

/**
 * Hook to detect when an element is visible in the viewport
 * @param options - Intersection Observer options
 * @returns tuple of [ref to attach to element, isVisible boolean]
 */
export const useIntersectionObserver = <T extends HTMLElement>(
  options: UseIntersectionObserverOptions = {},
): [React.RefObject<T>, boolean] => {
  const elementRef = useRef<T>(null)
  const [isVisible, setIsVisible] = useState(false)
  const hasTriggered = useRef(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Check if IntersectionObserver is available (SSR or older browsers)
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: assume element is visible
      setIsVisible(true)
      return
    }

    // If triggerOnce is true and we've already triggered, don't observe again
    if (options.triggerOnce && hasTriggered.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          setIsVisible(true)
          hasTriggered.current = true

          // If triggerOnce is true, disconnect after first trigger
          if (options.triggerOnce) {
            observer.disconnect()
          }
        } else {
          setIsVisible(false)
        }
      },
      {
        threshold: options.threshold ?? 0,
        rootMargin: options.rootMargin ?? '0px',
      },
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [options.threshold, options.rootMargin, options.triggerOnce])

  return [elementRef, isVisible]
}
